import agricarbonLogoWhite from '@/assets/images/agricarbon-logo-white.png';
import { cn } from '@/lib/utils';
import { NO_PERMISSIONS_MESSAGE } from '@/modules/Permissions';
import { routes } from '@/modules/Router';
import { ERROR_MESSAGES, ErrorType } from '@/types/errors';
import { copyToClipboard } from '@/utils/clipboard';
import { extractResourceContext, getErrorTypeFromApiError, normalizeError } from '@/utils/errorFallback';
import { createErrorEmailUrl, extractResourceContextForSharing, formatErrorForClipboard } from '@/utils/errorSharing';
import { captureException } from '@sentry/react';
import { ArrowLeft, HelpCircle, Home, LogIn, RefreshCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { isRouteErrorResponse, Link, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { toast } from 'react-toastify';

function Error() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();

  const normalizedError = normalizeError(error);
  const errorType = getErrorTypeFromApiError(normalizedError);
  const errorInfo = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];

  // Extract resource context from URL for debugging
  const resourceContext = extractResourceContext(location.pathname);

  useEffect(() => {
    captureException(error, {
      level: 'fatal',
      tags: {
        errorType,
        page: location.pathname,
        ...(resourceContext.customerId && { customerId: resourceContext.customerId }),
        ...(resourceContext.projectId && { projectId: resourceContext.projectId }),
        ...(resourceContext.farmId && { farmId: resourceContext.farmId }),
        ...(resourceContext.fieldId && { fieldId: resourceContext.fieldId }),
        ...(resourceContext.userId && { userId: resourceContext.userId }),
      },
      extra: {
        resourceContext,
        normalizedError,
      },
    });
  }, [error, errorType, location.pathname, resourceContext, normalizedError]);

  const getStatusCodeDisplay = () => {
    if (isRouteErrorResponse(error)) return error.status.toString();
    if (normalizedError.status) return normalizedError.status.toString();

    // Map error types to status codes for display
    switch (errorType) {
      case ErrorType.NOT_FOUND:
        return '404';
      case ErrorType.UNAUTHORIZED:
      case ErrorType.AUTHENTICATION_FAILED:
        return '401';
      case ErrorType.FORBIDDEN:
      case ErrorType.PERMISSION_DENIED:
        return '403';
      case ErrorType.INTERNAL_SERVER_ERROR:
        return '500';
      case ErrorType.BAD_REQUEST:
      case ErrorType.VALIDATION_ERROR:
        return '400';
      case ErrorType.CONFLICT:
        return '409';
      default:
        return 'ERROR';
    }
  };

  const getErrorMessage = useCallback(() => {
    // Handle the special permissions case
    if (normalizedError.message === NO_PERMISSIONS_MESSAGE) {
      return "You don't have permission to access this page.";
    }

    return normalizedError.message || errorInfo.description;
  }, [normalizedError.message, errorInfo.description]);

  const getErrorTitle = () => {
    if (normalizedError.message === NO_PERMISSIONS_MESSAGE) {
      return 'Access Denied';
    }
    return errorInfo.title;
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const showRetry = [ErrorType.NETWORK_ERROR, ErrorType.TIMEOUT, ErrorType.INTERNAL_SERVER_ERROR].includes(errorType);

  const showLogin = [ErrorType.UNAUTHORIZED, ErrorType.AUTHENTICATION_FAILED].includes(errorType);

  const supportEmailUrl = useMemo(() => {
    const sharingContext = extractResourceContextForSharing(location.pathname);

    return createErrorEmailUrl({
      timestamp: new Date().toLocaleString(),
      errorType,
      errorMessage: getErrorMessage(),
      statusCode: normalizedError.status,
      resourceContext: sharingContext,
      pathname: location.pathname,
    });
  }, [errorType, location.pathname, normalizedError.status, getErrorMessage]);

  const handleCopyToClipboard = useCallback(async () => {
    const sharingContext = extractResourceContextForSharing(location.pathname);

    const errorInfo = {
      timestamp: new Date().toLocaleString(),
      errorType,
      errorMessage: getErrorMessage(),
      statusCode: normalizedError.status,
      resourceContext: sharingContext,
      pathname: location.pathname,
    };

    const clipboardText = formatErrorForClipboard(errorInfo);
    const success = await copyToClipboard(clipboardText);

    if (success) {
      toast.success('Error info copied to clipboard', {
        autoClose: 2000,
      });
    } else {
      toast.error('Failed to copy error to clipboard', {
        autoClose: 2000,
      });
    }
  }, [errorType, location.pathname, normalizedError.status, getErrorMessage]);

  return (
    <div className={cn('min-h-screen bg-[#4F5D4F] flex flex-col items-center justify-center p-4')}>
      <div className={cn('flex items-center mb-8')}>
        <img src={agricarbonLogoWhite} alt="Agricarbon Logo" className={cn('h-12 object-contain')} />
      </div>

      <div className={cn('text-center mb-8 max-w-2xl')}>
        <h1 className={cn('text-white text-6xl md:text-8xl font-bold mb-4')}>{getStatusCodeDisplay()}</h1>

        <h2 className={cn('text-white text-2xl md:text-3xl font-semibold mb-4')}>{getErrorTitle()}</h2>

        <p className={cn('text-white/90 text-lg md:text-xl')}>{getErrorMessage()}</p>
      </div>

      {/* Developer Context - Show most specific resource ID if available */}
      {Object.keys(resourceContext).length > 0 && (
        <div className={cn('mb-6 p-4 bg-black/20 rounded-lg border border-white/10 max-w-lg')}>
          <h3 className={cn('text-white/70 text-sm font-medium mb-2')}>Resource Context</h3>
          <div className={cn('space-y-1 text-xs text-white/60 font-mono')}>
            {resourceContext.fieldId ? (
              <div>
                Field ID: <span className={cn('text-white/80')}>{resourceContext.fieldId}</span>
              </div>
            ) : resourceContext.farmId ? (
              <div>
                Farm ID: <span className={cn('text-white/80')}>{resourceContext.farmId}</span>
              </div>
            ) : resourceContext.projectId ? (
              <div>
                Project ID: <span className={cn('text-white/80')}>{resourceContext.projectId}</span>
              </div>
            ) : resourceContext.customerId ? (
              <div>
                Customer ID: <span className={cn('text-white/80')}>{resourceContext.customerId}</span>
              </div>
            ) : resourceContext.userId ? (
              <div>
                User ID: <span className={cn('text-white/80')}>{resourceContext.userId}</span>
              </div>
            ) : null}
            <div>
              Path: <span className={cn('text-white/80')}>{location.pathname}</span>
            </div>
          </div>
        </div>
      )}

      <div className={cn('flex flex-wrap gap-4 justify-center')}>
        {showRetry && (
          <button
            onClick={handleRetry}
            className={cn(
              'flex items-center gap-2 bg-[#8B9B8B] hover:bg-[#7A8A7A] transition-colors',
              'px-6 py-3 rounded-md text-white font-medium'
            )}
          >
            <RefreshCcw className={cn('w-5 h-5')} />
            <span>Try Again</span>
          </button>
        )}

        {showLogin ? (
          <Link
            to={`/login${window.location.pathname !== '/' ? `?redirect=${encodeURIComponent(window.location.pathname)}` : ''}`}
            className={cn(
              'flex items-center gap-2 bg-[#8B9B8B] hover:bg-[#7A8A7A] transition-colors',
              'px-6 py-3 rounded-md text-white font-medium no-underline'
            )}
          >
            <LogIn className={cn('w-5 h-5')} />
            <span>Sign In</span>
          </Link>
        ) : (
          <button
            onClick={handleGoBack}
            className={cn(
              'flex items-center gap-2 border border-white/30 text-white hover:bg-white/10',
              'px-14 py-3 rounded-md font-medium transition-colors'
            )}
          >
            <ArrowLeft className={cn('w-5 h-5')} />
            <span>Go Back</span>
          </button>
        )}

        <Link
          to={routes.home}
          className={cn(
            'flex items-center gap-2 bg-[#8B9B8B] hover:bg-[#7A8A7A] transition-colors',
            'px-10 py-3 rounded-md text-white font-medium no-underline'
          )}
        >
          <Home className={cn('w-5 h-5')} />
          <span>Return Home</span>
        </Link>

        {/* Show contact support for all error types except authentication-related ones */}
        {![ErrorType.UNAUTHORIZED, ErrorType.AUTHENTICATION_FAILED, ErrorType.FORBIDDEN, ErrorType.PERMISSION_DENIED].includes(
          errorType
        ) && (
          <a
            href={supportEmailUrl}
            className={cn(
              'flex items-center gap-2 border border-white/30 text-white hover:bg-white/10',
              'px-8 py-3 rounded-md font-medium transition-colors no-underline'
            )}
          >
            <HelpCircle className={cn('w-5 h-5')} />
            <span>Contact Support</span>
          </a>
        )}
      </div>

      {/* Copy to clipboard link - positioned between buttons and timestamp */}
      {![ErrorType.UNAUTHORIZED, ErrorType.AUTHENTICATION_FAILED, ErrorType.FORBIDDEN, ErrorType.PERMISSION_DENIED].includes(errorType) && (
        <button
          onClick={handleCopyToClipboard}
          className={cn('text-white/70 hover:text-white hover:underline cursor-pointer', 'text-sm transition-colors mt-6')}
        >
          Click here to copy info to clipboard
        </button>
      )}

      <div className={cn('mt-6 text-white/50 text-xs text-center')}>
        <p>Timestamp: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
}

export default Error;
